import React, { useContext, useEffect } from "react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { ThemeContext } from "../../../context/ThemeContext";
import "./Privacy.css";
import AOS from "aos";
import "aos/dist/aos.css";

const Privacy = () => {
  const { darkMode } = useContext(ThemeContext);
  const [scrolled, setScrolled] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState("privacy");

  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 800,
      once: false,
      mirror: true,
    });

    const handleScroll = () => {
      if (window.scrollY > 80) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Smooth scroll function
  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const targetSection = document.getElementById(targetId);
    if (targetSection) {
      const offsetTop =
        targetSection.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className={`privacy-container ${darkMode ? "dark-mode" : ""}`}>
      <Navbar
        scrolled={scrolled}
        activeSection={activeSection}
        handleNavClick={handleNavClick}
      />

      <div className="privacy-content" id="privacy">
        <div className="container py-5 mt-5">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="privacy-header" data-aos="fade-up">
                <h1 className="privacy-title">Privacy Policy</h1>
                <p className="privacy-subtitle">
                  Last updated: {new Date().toLocaleDateString()}
                </p>
              </div>

              <div
                className="privacy-unified"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                <p>
                  At NUMRA, we collect certain personal identifiers and
                  operational data necessary to provide our financial management
                  services. This includes authentication credentials, contact
                  information, and account details required for user
                  identification and communication purposes. We also securely
                  store financial data that users choose to input into our
                  system for accounting and record-keeping purposes. Our systems
                  automatically collect technical information including usage
                  analytics and operational logs that help us monitor system
                  performance and enhance user experience. For transaction
                  processing, we utilize Paddle as our payment gateway, which
                  securely manages payment method details and billing
                  information.
                </p>

                <p>
                  The information we collect is essential to delivering and
                  continuously improving NUMRA's accounting and financial
                  management services. We utilize this data to provide
                  personalized user experiences, enhance functionality, and
                  maintain optimal system performance. We may use contact
                  information to communicate important updates, security
                  notices, and account information. Our team analyzes usage
                  patterns and system metrics to identify areas for improvement
                  and optimize service delivery. Additionally, we implement
                  robust data processing practices to ensure compliance with
                  relevant financial regulations and maintain the highest
                  standards of security throughout our platform.
                </p>

                <p>
                  We partner with Paddle as our official payment processing
                  provider. When conducting financial transactions through
                  NUMRA, your payment information is securely processed by
                  Paddle in accordance with their privacy policy and stringent
                  security protocols. Paddle may collect and process
                  transaction-related data such as payment method details,
                  transaction amounts, and billing addresses as required to
                  complete purchases and manage subscriptions. In addition to
                  Paddle, we utilize select trusted third-party services for
                  cloud hosting, performance optimization, and analytical
                  purposes. All our technology partners are carefully evaluated
                  to ensure they maintain compliance with international data
                  protection standards including GDPR, CCPA, and other
                  applicable regulations. These service providers operate under
                  strict confidentiality obligations and implement
                  industry-leading security measures to safeguard your
                  information.
                </p>

                <p>
                  NUMRA employs cookies and similar technologies to enhance your
                  user experience and provide essential functionality. These
                  technologies enable us to authenticate user sessions, maintain
                  security parameters, and remember user preferences. We also
                  utilize performance and analytics cookies to gather insights
                  into how users interact with our platform, allowing us to
                  identify potential improvements and optimize our services.
                  While most browsers automatically accept cookies, you maintain
                  control over your cookie settings and can modify your
                  preferences through your browser settings. Please note that
                  disabling certain cookies may impact specific features and
                  functionalities of the NUMRA platform.
                </p>

                <p>
                  We implement comprehensive security measures to safeguard your
                  information against unauthorized access, alteration,
                  disclosure, or destruction. Our multi-layered approach to data
                  protection includes industry-standard encryption for data in
                  transit and at rest, secure authentication protocols, and
                  role-based access controls that restrict data access to
                  authorized personnel only. We regularly conduct security
                  assessments and penetration testing to identify and address
                  potential vulnerabilities. Our development practices follow
                  security-by-design principles, ensuring that data protection
                  is integrated into our systems from the ground up rather than
                  added as an afterthought.
                </p>

                <p>
                  NUMRA retains user information for the duration of the
                  account's active status to ensure seamless service delivery
                  and continuity. When an account is deactivated or deleted, we
                  implement a structured data archiving process that preserves
                  certain information for a limited period as required by
                  applicable financial regulations, tax laws, and legitimate
                  business purposes. This archived data is maintained with the
                  same level of security as active accounts and is only
                  accessible to authorized personnel for specific regulatory or
                  analytical purposes. After the retention period expires,
                  personal data is securely and permanently deleted from our
                  systems through established data purging protocols.
                </p>

                <p>
                  We respect and uphold the data privacy rights of all our
                  users. You maintain the right to access your personal
                  information stored in our systems and can request
                  comprehensive details about how your data is being processed.
                  Through your account settings or by contacting our privacy
                  team, you can update or correct any inaccurate information.
                  Should you wish to exercise your right to data portability,
                  deletion, or restriction of processing, we have established
                  efficient procedures to handle such requests in accordance
                  with applicable data protection laws. Additionally, you retain
                  the right to withdraw previously given consent for data
                  processing at any time, and we are committed to honoring such
                  requests promptly while ensuring continued compliance with our
                  legal obligations.
                </p>

                <p>
                  NUMRA's services are designed exclusively for adult users and
                  business professionals. We do not direct our services toward,
                  nor do we knowingly solicit or collect personal information
                  from individuals under 18 years of age. Our terms of service
                  explicitly prohibit underage users from accessing or utilizing
                  our platform. If we discover that we have inadvertently
                  collected personal information from a minor, we promptly
                  initiate our data deletion protocol to remove such information
                  from our systems. We encourage parents and guardians to
                  monitor their children's online activities and help enforce
                  this policy by instructing children never to provide personal
                  information on our platform and contacting us if they believe
                  a child has submitted personal information to NUMRA.
                </p>

                <p>
                  This Privacy Policy may undergo periodic revisions to reflect
                  changes in our practices, service offerings, or regulatory
                  requirements. When we implement significant changes that
                  materially alter how we collect, use, or share your
                  information, we will provide clear notification through
                  multiple channels. Users will receive direct communication via
                  their registered email address and/or prominent in-application
                  notifications that outline the nature and impact of these
                  changes. The revised policy will include an updated effective
                  date, and in some cases, we may provide advance notice before
                  implementing substantial modifications. We encourage users to
                  review our Privacy Policy periodically to stay informed about
                  our data practices and their privacy rights.
                </p>

                <p>
                  If you have any questions, concerns, or requests regarding
                  this Privacy Policy or our data practices, please contact our
                  dedicated privacy team at{" "}
                  <a href="mailto:privacy@numra.com">privacy@numra.com</a>. We
                  are committed to addressing all inquiries promptly and
                  thoroughly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Privacy;
